import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, getDocs } from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import { db } from "../Config/firebase";
import { consultaTenantAtual } from "../Utils/tenant";

function formatarData(data) {
  const [ano, mes, dia] = String(data || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : "-";
}

export default function CadastroVerificacaoCompetencias() {
  const history = useHistory();
  const [registros, setRegistros] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [selecionando, setSelecionando] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [colaborador, setColaborador] = useState(null);
  const [carregandoColaboradores, setCarregandoColaboradores] = useState(false);
  const [erroColaboradores, setErroColaboradores] = useState("");

  useEffect(() => {
    if (!selecionando) return;
    let ativo = true;
    setColaborador(null);
    setColaboradores([]);
    setErroColaboradores("");
    setCarregandoColaboradores(true);
    async function carregarColaboradores() {
      try {
        const snap = await getDocs(consultaTenantAtual(collection(db, "dp_funcionarios")));
        const lista = snap.docs.map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => !item.dataDemissao && item.ativo !== false)
          .map((item) => ({ id: item.id, nome: item.nome || "", cargo: item.cargo || "" }))
          .filter((item) => item.nome.trim())
          .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
        if (ativo) setColaboradores(lista);
      } catch (error) {
        console.error("Erro ao carregar colaboradores do DP:", error);
        if (ativo) setErroColaboradores("Não foi possível carregar os colaboradores do Departamento Pessoal.");
      } finally {
        if (ativo) setCarregandoColaboradores(false);
      }
    }
    carregarColaboradores();
    return () => { ativo = false; };
  }, [selecionando]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      try {
        const snap = await getDocs(consultaTenantAtual(collection(db, "tblVerificacaoCompetencias")));
        const fichas = snap.docs.map((item) => ({ ...item.data(), id: item.id }));
        fichas.sort((a, b) => String(b.data || "").localeCompare(String(a.data || "")) || (b.criadoEm?.toMillis?.() || 0) - (a.criadoEm?.toMillis?.() || 0));
        if (ativo) setRegistros(fichas);
      } catch (error) {
        console.error("Erro ao carregar fichas de competências:", error);
        if (ativo) setErro("Não foi possível carregar as fichas. Verifique sua conexão e as permissões de acesso.");
      } finally {
        if (ativo) setCarregando(false);
      }
    }
    carregar();
    return () => { ativo = false; };
  }, []);

  const filtrados = useMemo(() => {
    const termo = filtro.trim().toLocaleLowerCase("pt-BR");
    return registros.filter((item) => [item.avaliado, item.avaliador, item.cargoFuncao, item.conclusao, formatarData(item.data)]
      .some((valor) => contemTextoPesquisa(String(valor || "").toLocaleLowerCase("pt-BR"), termo)));
  }, [registros, filtro]);

  const abrir = (id) => history.push(`/app/verificacao-competencias/${id}`);

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, mb: 2, p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Verificação de Competências e Habilidades</Typography>
              <Typography variant="body2" color="text.secondary">RQ 5.3 — Fichas de avaliação individual de competências técnicas e comportamentais.</Typography>
            </Box>
            <Button variant="contained" onClick={() => setSelecionando(true)}>Nova ficha</Button>
          </Stack>
          <TextField label="Pesquisar por avaliado, avaliador, cargo, data ou conclusão" value={filtro} onChange={(event) => setFiltro(event.target.value)} size="small" fullWidth sx={{ mt: 2 }} />
        </Paper>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        <Paper sx={{ border: "1px solid #e2e8f0", overflow: "auto" }}>
          <Table size="small" sx={{ minWidth: 850 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>{["Avaliado", "Data", "Avaliador", "Cargo/Função", "Conclusão", "Ações"].map((titulo) => <TableCell key={titulo}>{titulo}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {filtrados.map((item) => (
                <TableRow key={item.id} hover onClick={() => abrir(item.id)} sx={{ cursor: "pointer" }}>
                  <TableCell>{item.avaliado || "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatarData(item.data)}</TableCell>
                  <TableCell>{item.avaliador || "-"}</TableCell>
                  <TableCell>{item.cargoFuncao || "-"}</TableCell>
                  <TableCell>{item.conclusao || "Não informada"}</TableCell>
                  <TableCell><Tooltip title="Visualizar relatório"><IconButton aria-label={`Visualizar relatório de ${item.avaliado}`} size="small" onClick={(event) => { event.stopPropagation(); history.push(`/app/verificacao-competencias/${item.id}?print=1`); }}><VisibilityIcon color="primary" /></IconButton></Tooltip></TableCell>
                </TableRow>
              ))}
              {carregando && <TableRow><TableCell colSpan={6} align="center"><Stack alignItems="center" spacing={1} sx={{ py: 4 }}><CircularProgress /><Typography>Carregando fichas...</Typography></Stack></TableCell></TableRow>}
              {!carregando && !erro && filtrados.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nenhuma ficha encontrada.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      </Box>
      <Dialog open={selecionando} onClose={() => setSelecionando(false)} fullWidth maxWidth="sm">
        <DialogTitle>Selecionar colaborador avaliado</DialogTitle>
        <DialogContent>
          {erroColaboradores && <Alert severity="error" sx={{ mb: 2 }}>{erroColaboradores}</Alert>}
          <Autocomplete
            sx={{ mt: 1 }}
            options={colaboradores}
            value={colaborador}
            onChange={(_, valor) => setColaborador(valor)}
            getOptionLabel={(item) => item.cargo ? `${item.nome} — ${item.cargo}` : item.nome}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, item) => (
              <li {...props} key={item.id}>
                <Box sx={{ minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }}>
                  <Typography fontWeight={600}>{item.nome}</Typography>
                  <Typography variant="body2" color="text.secondary">Função: {item.cargo || "Não informada"}</Typography>
                </Box>
              </li>
            )}
            loading={carregandoColaboradores}
            loadingText="Carregando colaboradores..."
            noOptionsText="Nenhum colaborador encontrado no Departamento Pessoal"
            renderInput={(params) => <TextField {...params} label="Colaborador do Departamento Pessoal" autoFocus />}
          />
          {colaborador && (
            <Box sx={{ mt: 2, whiteSpace: "normal", overflowWrap: "anywhere" }}>
              <Typography><strong>Nome completo:</strong> {colaborador.nome}</Typography>
              <Typography><strong>Função:</strong> {colaborador.cargo || "Não informada"}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelecionando(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!colaborador || carregandoColaboradores || Boolean(erroColaboradores)} onClick={() => history.push(`/app/verificacao-competencias/novo?funcionarioId=${encodeURIComponent(colaborador.id)}`)}>Continuar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
